import React from 'react'
import i18n from 'sharedKernel/i18n/translate/config'
import { trans } from 'sharedKernel/i18n'
import { IPublicMeetingRoom } from '../types'
import {
  CapacityIcon,
  CapacityRow,
  Card,
  CardBody,
  CardDescription,
  CardImage,
  CardImageButton,
  CardTitle,
  SelectButton,
  SelectButtonIcon,
} from './style'

interface SpaceCardProps {
  room: IPublicMeetingRoom
  isSelected: boolean
  isSelectionDisabled: boolean
  onToggle: (roomId: string) => void
  onImageClick?: (room: IPublicMeetingRoom) => void
}

const SpaceCardComponent = ({
  room,
  isSelected,
  isSelectionDisabled,
  onToggle,
  onImageClick,
}: SpaceCardProps): JSX.Element => {
  const isButtonDisabled = isSelectionDisabled && !isSelected
  const hasGallery = room.galleryImages.length > 0
  const canOpenGallery = hasGallery && typeof onImageClick === 'function'

  const labels = {
    select: trans('meetingRoom.public.select'),
    selected: trans('meetingRoom.public.selected'),
    capacity: i18n.t('meetingRoom.public.capacity', { count: room.maxCapacity }),
    viewImages: i18n.t('meetingRoom.public.viewImages', { name: room.name }),
  }

  const handleImageClick = (): void => {
    if (!canOpenGallery) return
    onImageClick(room)
  }

  return (
    <Card>
      {room.imageUrl !== '' && (
        canOpenGallery ? (
          <CardImageButton
            type="button"
            onClick={handleImageClick}
            aria-label={labels.viewImages}
          >
            <CardImage src={room.imageUrl} alt={room.name} loading="lazy" />
          </CardImageButton>
        ) : (
          <CardImage src={room.imageUrl} alt={room.name} loading="lazy" />
        )
      )}
      <CardBody>
        <CardTitle>{room.name}</CardTitle>
        {room.description !== '' && (
          <CardDescription>{room.description}</CardDescription>
        )}
        <CapacityRow>
          <CapacityIcon icon={['fas', 'signal']} />
          {labels.capacity}
        </CapacityRow>
        <SelectButton
          type="button"
          $isSelected={isSelected}
          $isDisabled={isButtonDisabled}
          disabled={isButtonDisabled}
          onClick={() => onToggle(room.id)}
          aria-pressed={isSelected}
        >
          {isSelected ? (
            <>
              {labels.selected}
              <SelectButtonIcon icon={['fas', 'check']} />
            </>
          ) : (
            labels.select
          )}
        </SelectButton>
      </CardBody>
    </Card>
  )
}

export const SpaceCard = React.memo(SpaceCardComponent)
